import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { COLLECTIONS } from '../constants/constants';
import { syncMyClaimsFromProfile } from './authUtils';

/**
 * JWT / AuthContext claims — aligned with backend verifyContractsAccess.
 * @param {object} claims
 * @returns {boolean}
 */
export function hasContractsClaims(claims = {}) {
  return (
    claims.admin === true ||
    claims.operations === true ||
    claims.role === 'admin' ||
    claims.role === 'operations'
  );
}

/**
 * @param {object|null} contextUser AuthContext user with .claims
 * @returns {boolean}
 */
export function hasContractsAccessFromContextUser(contextUser) {
  if (!contextUser) {
    return false;
  }
  return (
    hasContractsClaims(contextUser.claims || {}) ||
    contextUser.isAdmin === true ||
    contextUser.isOperations === true
  );
}

/**
 * Whether the user may access Contracts (matches backend verifyContractsAccess).
 * @param {import('firebase/auth').User} firebaseUser
 * @returns {Promise<boolean>}
 */
export async function checkContractsAccess(firebaseUser) {
  if (!firebaseUser?.uid) {
    return false;
  }

  let tokenResult = await firebaseUser.getIdTokenResult(false);
  if (hasContractsClaims(tokenResult.claims || {})) {
    return true;
  }

  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, firebaseUser.uid));
    const role = userDoc.data()?.role;
    if (role === 'admin' || role === 'operations') {
      return true;
    }
  } catch {
    // Ignore Firestore errors; try refreshed token below
  }

  tokenResult = await firebaseUser.getIdTokenResult(true);
  if (hasContractsClaims(tokenResult.claims || {})) {
    return true;
  }

  try {
    const syncResult = await syncMyClaimsFromProfile(firebaseUser);
    if (syncResult?.success) {
      tokenResult = await firebaseUser.getIdTokenResult(true);
      if (hasContractsClaims(tokenResult.claims || {})) {
        return true;
      }
    }
  } catch {
    // Fall through to final denial
  }

  return false;
}

/** Sync nav check from JWT claims. */
export function canShowContractsInNav(claims = {}) {
  return hasContractsClaims(claims);
}
